import { format } from 'svelte-i18n';
import { get } from 'svelte/store';

/**
 * Calculate the number of days remaining until a given end date (timestamp in ms).
 * Returns 0 if the end date has already passed.
 */
export const getExpiryDaysRemaining = (endDate: number): number => {
	const now = Date.now();
	if (endDate <= now) return 0;
	return Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
};

/**
 * Format an expiry timestamp or date string into "EXPIRES IN X DAYS" text.
 * Returns undefined if no valid expiry or already expired.
 */
export const formatExpiryText = (
	expiry: number | string | undefined,
): { key: string; object: object } | undefined => {
	if (!expiry) return undefined;

	let expiryMs: number;
	if (typeof expiry === 'number') {
		// Handle both seconds and milliseconds timestamps
		expiryMs = expiry > 1e12 ? expiry : expiry * 1000;
	} else {
		const parsed = new Date(expiry);
		if (isNaN(parsed.getTime())) return undefined;
		expiryMs = parsed.getTime();
	}

	const days = getExpiryDaysRemaining(expiryMs);
	if (days <= 0) return undefined;
	return days === 1
		? { key: 'text_expires_in_1_day', object: { default: 'Expires in 1 day' } }
		: {
				key: 'text_expires_in_days',
				object: { default: `Expires in {days} days`, values: { days } },
			};
};

/**
 * Get "EXPIRES IN X DAYS" text from a PROMOTIONS END_DATE timestamp.
 */
export const getPromoExpiryText = (endDate: number): { key: string; object: object } | undefined => {
	const days = getExpiryDaysRemaining(endDate);
	if (days <= 0) return undefined;
	return days === 1
		? { key: 'text_expires_in_1_day', object: { default: 'Expires in 1 day' } }
		: {
				key: 'text_expires_in_days',
				object: { default: `Expires in {days} days`, values: { days } },
			};
};
